import useRequest from "@/lib/request/useRequest";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
    NitrogenPlan,
    NitrogenPlanSimulation,
    ProcessingStatus,
    SimulationStatistics,
} from "@/types/fieldNitrogenPlan";
import { NitrogenSimulationType } from "@/types/enums";
import { SortingState } from "@tanstack/react-table";
import dayjs from "dayjs";
import {
    Autocomplete,
    AutocompleteItem,
    Button,
    ButtonGroup,
    Chip,
    ModalBody,
    ModalHeader,
    Spinner,
    Alert,
} from "@heroui/react";
import TableV2 from "@/components/table/TableV2";
import { roundTo } from "@/lib/utils/numbers";
import {
    getSimulationTypeColor,
    getSimulationTypeLabel,
    getSimulationTypeSortValue,
} from "@/lib/utils/nitrogenPlan";
import InfoModal from "../common/InfoModal";
import {
    faCog,
    faInfoCircle,
    faRotate,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    CartesianGrid,
    ComposedChart,
    Line,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { Field } from "@/types/field";
import { Key } from "@react-types/shared";
import { getTodayWithTimeTravel } from "@/lib/utils/date";
import useAuth from "@/lib/auth/useAuth";
import { sortArrayByKey } from "@/lib/utils/sorting";
import {
    CORN_CROP_KEY,
    CropConfiguration,
    CropStage,
} from "@/types/cropConfiguration";
import { RankingCell } from "../table/RankingCell";
import { Link, useNavigate } from "@tanstack/react-router";
import allNHorizontalImage from "@/assets/images/all_n_horizontal.png";
import { CropCycleV2 } from "@/types/cropCycle";
import { useI18n } from "@/lib/i18n/useI18n";
import { EnrollmentTier } from "@/lib/utils/enrollment";
import Upgrade from "../enrollment/Upgrade";
import useIsMobile from "@/lib/utils/useIsMobile";
import AdvisorParams from "./AdvisorParams";
import ProductAutoComplete from "../inputs/ProductAutoComplete";

type TableType = NitrogenPlanSimulation & {
    penalty_early_CWAD_ranking: number;
    penalty_NIAD_ranking: number;
    penalty_remob_ranking: number;
    penalty_roi_ranking: number;
    penalty_early_NUPC_ranking: number;
};

function getRankings(
    simulations: NitrogenPlanSimulation[],
    key: (typeof penaltyKeys)[number],
) {
    const values = simulations.map((sim, idx) => ({
        idx,
        value: sim[key],
    }));
    values.sort((a, b) => a.value - b.value);
    const rankings = Array(simulations.length).fill(0);
    let rank = 1;
    for (let i = 0; i < values.length; i++) {
        if (i > 0 && values[i].value !== values[i - 1].value) {
            rank = i + 1;
        }
        rankings[values[i].idx] = rank;
    }
    return rankings;
}

const CHART_KEYS = [
    { key: "nitrogen_uptake", label: "N Uptake" },
    { key: "CWAD", label: "Biomass" },
    {
        key: "soil_residual_nitrogen_surplus",
        label: "Residual N",
    },
    { key: "remobilization", label: "Translocation" },
];

const penaltyKeys = [
    "penalty_early_CWAD",
    "penalty_NIAD",
    "penalty_remob",
    "penalty_roi",
    "penalty_early_NUPC",
] as const;

function FieldNitrogenAdvisor({
    fieldId,
    onClose,
    hideCloseButton = false,
}: {
    fieldId: string;
    onClose: () => void;
    hideCloseButton?: boolean;
}) {
    const { isConsultantOrSentinel, userIsSentinel } = useAuth();
    const { useGetRequest, useMutateRequest } = useRequest();

    const fieldQuery = useGetRequest<Field>({
        url: `/fields/${fieldId}/`,
        enabled: !!fieldId,
    });
    const advisorEligible = useMemo(() => {
        return (
            (fieldQuery.data?.active_enrollment?.enrollment_tier !==
                EnrollmentTier.Monitoring &&
                fieldQuery.data?.active_enrollment?.enrollment_tier !==
                EnrollmentTier.Preview) ||
            isConsultantOrSentinel
        );
    }, [
        fieldQuery.data?.active_enrollment?.enrollment_tier,
        isConsultantOrSentinel,
    ]);
    const isPreview = useMemo(() => {
        return (
            fieldQuery.data?.active_enrollment?.enrollment_tier ===
            EnrollmentTier.Preview && !isConsultantOrSentinel
        );
    }, [
        fieldQuery.data?.active_enrollment?.enrollment_tier,
        isConsultantOrSentinel,
    ]);
    const [selectedProductId, setSelectedProductId] = useState<Key | null>(
        null,
    );
    const [selectedGrowthStageId, setSelectedGrowthStageId] =
        useState<Key | null>(null);
    useEffect(() => {
        if (fieldQuery.data) {
            setSelectedProductId(
                // 32% UAN = ID 2
                fieldQuery.data.default_product?.toString() ?? "2",
            );
        }
    }, [fieldQuery.data]);

    const today = useMemo(() => getTodayWithTimeTravel().utc(), []);

    const cropStageQuery = useGetRequest<CropStage>({
        url: `fields/${fieldId}/crop_stage/`,
        enabled: !!fieldId,
        params: {
            target_date: dayjs(today).format("YYYY-MM-DD"),
        },
    });

    const minimumAdvisorCropStageQuery = useGetRequest<CropStage>({
        url: `fields/${fieldId}/minimum_advisor_crop_stage/`,
        enabled: !!fieldId,
        params: {
            target_date: dayjs(today).format("YYYY-MM-DD"),
        },
    });

    const cropConfigQuery = useGetRequest<CropConfiguration>({
        url: `crop_configurations/${cropStageQuery.data?.crop_configuration_id}/`,
        enabled: !!cropStageQuery.data?.crop_configuration_id,
    });

    const isCorn = useMemo(() => {
        return cropConfigQuery.data?.crop_key === CORN_CROP_KEY;
    }, [cropConfigQuery.data?.crop_key]);

    const remainingGrowthStages = useMemo(() => {
        if (!cropConfigQuery.data?.stages || !minimumAdvisorCropStageQuery.data)
            return [];
        return sortArrayByKey(
            cropConfigQuery.data?.stages.filter(
                (stage) =>
                    stage.days_after_planting >=
                    minimumAdvisorCropStageQuery.data.days_after_planting,
            ) ?? [],
            "days_after_planting",
        );
    }, [cropConfigQuery.data?.stages, minimumAdvisorCropStageQuery.data]);

    const cropIsMature = useMemo(() => {
        return (
            cropStageQuery.data?.id ==
            cropConfigQuery.data?.stages.sort(
                (a, b) => b.days_after_planting - a.days_after_planting,
            )[0].id
        );
    }, [cropStageQuery.data?.id, cropConfigQuery.data?.stages]);

    useEffect(() => {
        if (!fieldQuery.isFetched) return;
        if (fieldQuery.data && fieldQuery.data.active_target_growth_stage) {
            const selectedStage = remainingGrowthStages.find(
                (stage) =>
                    stage.id === fieldQuery.data.active_target_growth_stage,
            );
            if (selectedStage) {
                setSelectedGrowthStageId(selectedStage.id.toString());
            } else {
                if (remainingGrowthStages.length > 0) {
                    setSelectedGrowthStageId(
                        remainingGrowthStages[0].id.toString(),
                    );
                }
            }
        } else {
            if (remainingGrowthStages.length > 0) {
                setSelectedGrowthStageId(
                    remainingGrowthStages[0].id.toString(),
                );
            }
        }
    }, [fieldQuery.data, fieldQuery.isFetched, remainingGrowthStages]);

    const [pollForResults, setPollForResults] = useState(false);

    const plan = useGetRequest<NitrogenPlan>({
        url: "/field_nitrogen_plans/query/",
        params: {
            field_id: fieldId,
            date: today.format("YYYY-MM-DD"),
            product_id: selectedProductId,
            growth_stage_id: selectedGrowthStageId,
        },
        enabled: !!selectedProductId && !!fieldId && !!selectedGrowthStageId,
        queryOptions: {
            retry: false,
            refetchInterval: pollForResults ? 10000 : undefined,
        },
    });

    useEffect(() => {
        if (!plan.data) return;
        setPollForResults(
            !(
                plan.data.processing_status === ProcessingStatus.COMPLETED ||
                plan.data.processing_status === ProcessingStatus.FAILED
            ),
        );
    }, [plan.data]);

    const statistics = useGetRequest<SimulationStatistics[]>({
        url: `/field_nitrogen_plans/${plan.data?.id}/statistics/`,
        params: {
            product_id: selectedProductId,
            growth_stage_id: selectedGrowthStageId,
        },
        enabled:
            !!plan.data?.id && !!selectedProductId && !!selectedGrowthStageId,
    });

    const generatePlan = useMutateRequest({
        url: "/field_nitrogen_plans/generate/",
        method: "put",
        errorMessage: "Failed to generate plan",
    });

    const [sorting, setSorting] = useState<SortingState>([
        {
            id: "simulation_type",
            desc: false,
        },
    ]);

    const tableData: TableType[] = useMemo(() => {
        const simulations = plan.data?.simulations.filter(
            (simulation) =>
                simulation.simulation_type !== NitrogenSimulationType.Max_Rate,
        );
        if (!simulations) return [];
        const rankingsByPenalty: Record<string, number[]> = {};
        for (const key of penaltyKeys) {
            rankingsByPenalty[key] = getRankings(simulations, key);
        }

        return simulations.map((simulation, i) => {
            return {
                ...simulation,
                penalty_early_CWAD_ranking:
                    rankingsByPenalty["penalty_early_CWAD"][i],
                penalty_NIAD_ranking: rankingsByPenalty["penalty_NIAD"][i],
                penalty_remob_ranking: rankingsByPenalty["penalty_remob"][i],
                penalty_roi_ranking: rankingsByPenalty["penalty_roi"][i],
                penalty_early_NUPC_ranking:
                    rankingsByPenalty["penalty_early_NUPC"][i],
            };
        });
    }, [plan.data?.simulations]);

    const prescriptionsQuery = useMutateRequest<
        {
            field_id: number;
            field_name: string;
            simulation_id: number;
            prescription_id: number;
            prescription_name: string;
        }[]
    >({
        url: "field_nitrogen_plans/simulations_prescriptions/",
        method: "put",
    });
    const navigate = useNavigate();

    const columns: ColumnDef<TableType>[] = [
        {
            id: "simulation_type",
            header: "Type",
            accessorFn: (row) => getSimulationTypeLabel(row.simulation_type),
            size: 150,
            cell: ({ row }) => {
                const color = getSimulationTypeColor(
                    row.original.simulation_type,
                );
                return (
                    <div
                        className="flex flex-row items-center gap-2 hover:scale-105 cursor-pointer transition-all duration-300 pl-2"
                        onClick={() => {
                            if (prescriptionsQuery.isPending) return;
                            prescriptionsQuery.mutate(
                                {
                                    data: { simulation_ids: [row.original.id] },
                                },
                                {
                                    onSuccess: (data) => {
                                        if (data.length > 0) {
                                            navigate({
                                                to: `/dashboard/fields/${fieldId}/rx/${data[0].prescription_id}`,
                                            });
                                        }
                                    },
                                },
                            );
                        }}
                    >
                        <div
                            className="rounded-md w-6 h-6 flex items-center justify-center "
                            style={{ backgroundColor: color }}
                        >
                            {prescriptionsQuery.isPending ? (
                                <Spinner size="sm" color="white" />
                            ) : null}
                        </div>
                        <span className="text-base font-semibold">
                            {getSimulationTypeLabel(
                                row.original.simulation_type,
                            )}
                        </span>
                    </div>
                );
            },
            sortingFn: (a, b) => {
                return (
                    getSimulationTypeSortValue(a.original.simulation_type) -
                    getSimulationTypeSortValue(b.original.simulation_type)
                );
            },
        },
        {
            id: "rate",
            header: "Rate",
            accessorFn: (row) => roundTo(row.current_rate, 0),
            cell: ({ row }) => {
                return (
                    <div className="flex flex-col items-start ">
                        <span className="text-xl font-semibold">
                            {roundTo(row.original.current_rate, 0)}
                        </span>
                        <span className="text-xs text-gray-500">
                            ({roundTo(row.original.minimum_rate, 0)} -{" "}
                            {roundTo(row.original.maximum_rate, 0)})
                        </span>
                    </div>
                );
            },
            size: 100,
        },
        {
            id: "Performance Rankings",
            header: () => {
                return (
                    <div>
                        <InfoModal
                            trigger={(setShowModal) => (
                                <div className="flex flex-row items-center  w-full gap-2">
                                    Performance Rankings
                                    <Button
                                        onPress={() => setShowModal(true)}
                                        size="sm"
                                        variant="light"
                                        color="primary"
                                        isIconOnly
                                    >
                                        <FontAwesomeIcon icon={faInfoCircle} />
                                    </Button>
                                </div>
                            )}
                        >
                            <ModalHeader className="font-semibold text-xl">
                                Performance Rankings
                            </ModalHeader>
                            <ModalBody>
                                <p>
                                    The performance rankings are calculated
                                    based on the following criteria:
                                </p>
                                <ul className="list-disc list-inside">
                                    <li>
                                        <span className="font-bold">
                                            Residual N:{" "}
                                        </span>
                                        Nitrogen remaining in the soil
                                    </li>
                                    <li>
                                        <span className="font-bold">
                                            N Uptake:{" "}
                                        </span>
                                        Cumulative crop nitrogen assimilation
                                    </li>
                                    <li>
                                        <span className="font-bold">
                                            Biomass:{" "}
                                        </span>
                                        Weight of vegetative biomass above the
                                        ground
                                    </li>
                                    <li>
                                        <span className="font-bold">
                                            Profit Potential:{" "}
                                        </span>
                                        Anticipated opportunity for profit
                                    </li>
                                    <li>
                                        <span className="font-bold">
                                            Translocation:{" "}
                                        </span>
                                        Natural movement of nitrogen from leaves
                                        and stalks to support grain development
                                    </li>
                                </ul>
                            </ModalBody>
                        </InfoModal>
                    </div>
                );
            },
            size: 500,
            columns: [
                {
                    id: "penalty_early_CWAD_ranking",
                    header: "Biomass",
                    accessorFn: (row) => row.penalty_early_CWAD_ranking,
                    cell: ({ row }) => (
                        <RankingCell
                            value={row.original.penalty_early_CWAD_ranking}
                        />
                    ),
                    size: 100,
                },
                {
                    id: "penalty_NIAD_ranking",
                    header: "Residual N",
                    accessorFn: (row) => row.penalty_NIAD_ranking,
                    cell: ({ row }) => (
                        <RankingCell
                            value={row.original.penalty_NIAD_ranking}
                        />
                    ),
                    size: 100,
                },
                {
                    id: "penalty_remob_ranking",
                    header: "Translocation",
                    accessorFn: (row) => row.penalty_remob_ranking,
                    cell: ({ row }) => (
                        <RankingCell
                            value={row.original.penalty_remob_ranking}
                        />
                    ),
                    size: 100,
                },
                {
                    id: "penalty_roi_ranking",
                    header: "Profit Potential",
                    accessorFn: (row) => row.penalty_roi_ranking,
                    size: 100,
                    cell: ({ row }) => (
                        <RankingCell value={row.original.penalty_roi_ranking} />
                    ),
                },
                {
                    id: "penalty_early_NUPC_ranking",
                    header: "N Uptake",
                    accessorFn: (row) => row.penalty_early_NUPC_ranking,
                    size: 100,
                    cell: ({ row }) => (
                        <RankingCell
                            value={row.original.penalty_early_NUPC_ranking}
                        />
                    ),
                },
            ],
        },
    ];

    const chartData = useMemo(() => {
        if (!plan.data || !statistics.data) return [];
        const maxRateSimulation = plan.data?.simulations.find(
            (simulation) =>
                simulation.simulation_type === NitrogenSimulationType.Max_Rate,
        );
        const simulationTypes = plan.data?.simulations.reduce(
            (acc, simulation) => {
                acc[simulation.id] = simulation;
                return acc;
            },
            {} as Record<number, NitrogenPlanSimulation>,
        );

        if (!maxRateSimulation || !simulationTypes) return [];

        const maxLookup = statistics.data
            ?.filter((stat) => stat.simulation === maxRateSimulation.id)
            .reduce(
                (acc, stat) => {
                    acc[stat.date] = {
                        date: stat.date,
                        nitrogen_uptake: stat.nitrogen_uptake,
                        nitrogen_applied: stat.nitrogen_applied,
                        CWAD: stat.CWAD,
                        NUPC: stat.NUPC,
                        soil_residual_nitrogen_surplus:
                            stat.soil_residual_nitrogen_surplus,
                        remobilization: stat.remobilization,
                    };
                    return acc;
                },
                {} as Record<
                    string,
                    {
                        date: string;
                        nitrogen_uptake: number;
                        nitrogen_applied: number;
                        CWAD: number;
                        NUPC: number;
                        soil_residual_nitrogen_surplus: number;
                        remobilization: number;
                    }
                >,
            );

        if (Object.keys(maxLookup).length === 0) return [];

        return sortArrayByKey(
            Object.values(
                statistics.data.reduce(
                    (acc, stat) => {
                        const simulationType =
                            simulationTypes[stat.simulation]?.simulation_type;
                        const updatedData = {
                            date: stat.date,
                            [`nitrogen_uptake__${simulationType}`]:
                                maxLookup[stat.date].nitrogen_uptake === 0
                                    ? 100
                                    : Math.max(
                                        0,
                                        Math.min(
                                            100,
                                            (stat.nitrogen_uptake /
                                                maxLookup[stat.date]
                                                    .nitrogen_uptake) *
                                            100,
                                        ),
                                    ),

                            [`nitrogen_applied__${simulationType}`]:
                                (stat.nitrogen_applied /
                                    maxLookup[stat.date].nitrogen_applied) *
                                100,
                            [`CWAD__${simulationType}`]:
                                (stat.CWAD / maxLookup[stat.date].CWAD) * 100,
                            [`NUPC__${simulationType}`]:
                                (stat.NUPC / maxLookup[stat.date].NUPC) * 100,
                            [`soil_residual_nitrogen_surplus__${simulationType}`]:
                                stat.soil_residual_nitrogen_surplus,
                            [`remobilization__${simulationType}`]:
                                stat.remobilization * 100,
                        };
                        acc[stat.date] = { ...acc[stat.date], ...updatedData };
                        return acc;
                    },
                    {} as Record<string, Record<string, number | string>>,
                ),
            ),
            "date",
        );
    }, [plan.data, statistics.data]);

    const [selectedChart, setSelectedChart] =
        useState<string>("nitrogen_uptake");

    const advisorProcessing = useMemo(() => {
        return (
            plan.data?.processing_status === ProcessingStatus.PROCESSING ||
            plan.data?.processing_status === ProcessingStatus.PENDING
        );
    }, [plan.data?.processing_status]);

    const advisorAvailable = useMemo(() => {
        const allTypes = isCorn
            ? Object.values(NitrogenSimulationType)
            : [NitrogenSimulationType.Sentinel];
        const presentTypes = (plan.data?.simulations ?? []).map(
            (simulation) => simulation.simulation_type,
        );
        return (
            allTypes.every((type) => presentTypes.includes(type)) &&
            plan.data?.simulations.every(
                (simulation) =>
                    simulation.processing_status === ProcessingStatus.COMPLETED,
            ) &&
            plan?.data?.processing_status === ProcessingStatus.COMPLETED &&
            !advisorProcessing
        );
    }, [
        advisorProcessing,
        isCorn,
        plan.data?.processing_status,
        plan.data?.simulations,
    ]);

    const advisorFailed = useMemo(() => {
        return (
            plan.data?.simulations.some(
                (simulation) =>
                    simulation.processing_status === ProcessingStatus.FAILED,
            ) || plan.data?.processing_status === ProcessingStatus.FAILED
        );
    }, [plan.data?.processing_status, plan.data?.simulations]);

    const advisorUngenerated = useMemo(() => {
        return (plan.data?.simulations ?? []).length === 0;
    }, [plan.data?.simulations]);

    const generate = useCallback(() => {
        generatePlan.mutate(
            {
                data: {
                    product: Number(selectedProductId),
                    field: Number(fieldId),
                    date: today.format("YYYY-MM-DD"),
                },
            },
            {
                onSuccess: () => {
                    plan.refetch();
                    statistics.refetch();
                    setPollForResults(true);
                },
            },
        );
    }, [generatePlan, selectedProductId, fieldId, today, plan, statistics]);

    const currentCropCycle = useGetRequest<CropCycleV2>({
        url: `fields/${fieldId}/current_crop_cycle/`,
    });

    const applicationOperations = useGetRequest<{
        operations: { id: number; average_N: number; start_date: string }[];
    }>({
        url: `fields/${fieldId}/application_operations/`,
        enabled: !!fieldId && !!currentCropCycle.data,
        params: {
            start_date: dayjs(currentCropCycle.data?.start_date).format(
                "YYYY-MM-DD",
            ),
            end_date: dayjs(getTodayWithTimeTravel()).format("YYYY-MM-DD"),
        },
    });

    const totalNApplied = useMemo(() => {
        return applicationOperations.data?.operations.reduce(
            (acc, operation) => acc + operation.average_N,
            0,
        );
    }, [applicationOperations.data?.operations]);

    const { formatUnit } = useI18n();

    const crm = useMemo(() => {
        if (!isCorn) return null;
        const firstVariety =
            currentCropCycle?.data?.effective_planting_operation?.varieties[0];
        return firstVariety?.crm;
    }, [
        currentCropCycle?.data?.effective_planting_operation?.varieties,
        isCorn,
    ]);

    const { isMobile } = useIsMobile();

    const [showAdvisorSoil, setShowAdvisorSoil] = useState(false);

    return advisorEligible ? (
        <div className="flex flex-col gap-2 pb-96 w-full">
            <div className="flex flex-row justify-between items-center">
                <div className="flex flex-row items-center gap-2">
                    <img
                        src={allNHorizontalImage}
                        alt="All N "
                        className="h-8 w-auto"
                    />
                    <div className="text-xl font-bold">Sentinel Advisor</div>
                </div>
                <div className="flex flex-row items-center gap-2 pr-10">
                    {userIsSentinel && (
                        <Button
                            size="sm"
                            variant="light"
                            color="primary"
                            isIconOnly={isMobile}
                            startContent={<FontAwesomeIcon icon={faCog} />}
                            onPress={() => setShowAdvisorSoil(true)}
                        >
                            {isMobile ? "" : "Advisor Parameters"}
                        </Button>
                    )}
                    {!hideCloseButton && (
                        <Button
                            onPress={onClose}
                            size="sm"
                            variant="light"
                            color="primary"
                            isIconOnly
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </Button>
                    )}
                </div>
            </div>
            <div className="flex flex-row flex-wrap gap-2">
                <Chip radius="sm" size="sm" variant="flat">
                    Planted:{" "}
                    {fieldQuery.data?.active_planting_date
                        ? dayjs(fieldQuery.data.active_planting_date).format(
                            "ll",
                        )
                        : "—"}
                    {cropConfigQuery.data
                        ? ` - ${cropConfigQuery.data.crop_name}`
                        : ""}
                </Chip>
                {crm && (
                    <Chip radius="sm" size="sm" variant="flat">
                        CRM: {crm}
                    </Chip>
                )}
                <Chip radius="sm" size="sm" variant="flat">
                    Estimated Growth Stage:{" "}
                    {cropStageQuery.data?.growth_stage ?? "—"}
                </Chip>
                <Chip radius="sm" size="sm" variant="flat">
                    N Applied: {totalNApplied ? roundTo(totalNApplied, 1) : "—"}{" "}
                    {formatUnit("lb_ac", "kg_ha")}
                </Chip>
            </div>

            {cropIsMature ? (
                <div>
                    <Alert
                        color="warning"
                        variant="flat"
                        title="Crop is mature"
                        classNames={{
                            title: "text-sm",
                            description: "text-xs",
                        }}
                        description={
                            <>
                                <p>
                                    This crop is estimated to be mature, so the
                                    Advisor is unavailable.
                                </p>
                                <p>
                                    Feel like this estimate is wrong?{" "}
                                    {currentCropCycle.data
                                        ?.effective_planting_operation?.id ? (
                                        <>
                                            <Link
                                                to="/dashboard/fields/$fieldId/operations/planting/$plantingOperationId"
                                                params={{
                                                    fieldId,
                                                    plantingOperationId:
                                                        currentCropCycle.data.effective_planting_operation.id.toString(),
                                                }}
                                                className="text-primary underline"
                                            >
                                                Edit the planting operation
                                            </Link>
                                            {" or "}
                                        </>
                                    ) : null}
                                    <Link
                                        to="/dashboard/fields/$fieldId/operations/add"
                                        params={{ fieldId }}
                                        search={{ type: "planting" }}
                                        className="text-primary underline"
                                    >
                                        add a planting operation
                                    </Link>
                                    .
                                </p>
                            </>
                        }
                    />
                </div>
            ) : (
                <>
                    <div className="flex flex-row justify-between gap-2 items-end rounded-md py-2">
                        <div className="flex flex-row gap-2">
                            <ProductAutoComplete
                                selectedProductId={
                                    selectedProductId as string | null
                                }
                                setSelectedProductId={setSelectedProductId}
                                isDisabled={generatePlan.isPending}
                            />
                            <Autocomplete
                                selectedKey={selectedGrowthStageId as Key}
                                onSelectionChange={setSelectedGrowthStageId}
                                label="Target Growth Stage"
                                labelPlacement="outside"
                                placeholder="Select Growth Stage"
                                size="sm"
                                isDisabled={generatePlan.isPending}
                                isLoading={plan.isLoading}
                            >
                                {(remainingGrowthStages ?? []).map(
                                    (growthStage) => (
                                        <AutocompleteItem
                                            key={growthStage.id.toString()}
                                        >
                                            {growthStage.growth_stage}
                                        </AutocompleteItem>
                                    ),
                                )}
                            </Autocomplete>
                        </div>

                        {(advisorAvailable || advisorProcessing) && (
                            <div>
                                <Button
                                    onPress={generate}
                                    size="sm"
                                    color="primary"
                                    isLoading={generatePlan.isPending}
                                    isDisabled={generatePlan.isPending}
                                    startContent={
                                        <FontAwesomeIcon icon={faRotate} />
                                    }
                                >
                                    Regenerate Advisor
                                </Button>
                            </div>
                        )}
                    </div>
                    {!plan.isLoading &&
                        !advisorProcessing &&
                        (advisorUngenerated || advisorFailed) && (
                            <div className="flex flex-col justify-between gap-4 items-center rounded-md py-2 text-center">
                                {!advisorFailed ? (
                                    <div className="text-gray-500 text-sm">
                                        Advisor has not been generated for this
                                        product
                                    </div>
                                ) : (
                                    <div>
                                        <div className="text-red-500 text-sm">
                                            Advisor processing failed
                                        </div>
                                        {!isCorn && (
                                            <div className="text-sm">
                                                This crop may not have recent
                                                quality imagery data to
                                                recommend a rate
                                            </div>
                                        )}
                                    </div>
                                )}
                                <Button
                                    onPress={generate}
                                    size="sm"
                                    color="primary"
                                    isLoading={
                                        generatePlan.isPending ||
                                        advisorProcessing
                                    }
                                    isDisabled={
                                        generatePlan.isPending ||
                                        advisorProcessing
                                    }
                                >
                                    Generate Advisor Data
                                </Button>
                            </div>
                        )}
                    {!plan.isLoading && advisorProcessing && (
                        <div className="flex flex-col justify-between gap-4 items-center rounded-md py-2">
                            <div className="text-gray-500 text-sm">
                                Advisor is processing
                            </div>
                            <Spinner />
                        </div>
                    )}
                    {advisorAvailable && (
                        <>
                            {isCorn && (
                                <>
                                    <div className="flex flex-row ">
                                        <ButtonGroup size="sm">
                                            {CHART_KEYS.map(
                                                ({ key, label }) => (
                                                    <Button
                                                        key={key}
                                                        color={
                                                            selectedChart ===
                                                                key
                                                                ? "primary"
                                                                : "default"
                                                        }
                                                        onPress={() =>
                                                            setSelectedChart(
                                                                key,
                                                            )
                                                        }
                                                    >
                                                        {label}
                                                    </Button>
                                                ),
                                            )}
                                        </ButtonGroup>
                                    </div>
                                    <div className="overflow-x-scroll md:overflow-x-hidden">
                                        <ResponsiveContainer
                                            width={isMobile ? 600 : "100%"}
                                            height={300}
                                        >
                                            <ComposedChart
                                                margin={{
                                                    top: 10,
                                                    right: 0,
                                                    left: 10,
                                                    bottom: 10,
                                                }}
                                                data={chartData}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis
                                                    fontSize={12}
                                                    dataKey="date"
                                                    type="category"
                                                    angle={-24}
                                                    dy={16}
                                                    tickFormatter={(value) =>
                                                        dayjs(value).format(
                                                            "MMM D",
                                                        )
                                                    }
                                                />
                                                <YAxis
                                                    label={{
                                                        value:
                                                            CHART_KEYS.find(
                                                                (key) =>
                                                                    key.key ===
                                                                    selectedChart,
                                                            )?.label ?? "",
                                                        angle: -90,
                                                        position: "outside",
                                                        dx: -25,
                                                    }}
                                                    tickFormatter={(value) => {
                                                        if (
                                                            selectedChart !==
                                                            "soil_residual_nitrogen_surplus"
                                                        ) {
                                                            return `${value}%`;
                                                        }
                                                        return `${value} lbs`;
                                                    }}
                                                    fontSize={12}
                                                />
                                                {Object.values(
                                                    NitrogenSimulationType,
                                                ).map((simulationType) => (
                                                    <Line
                                                        id={simulationType}
                                                        key={simulationType}
                                                        dataKey={`${selectedChart}__${simulationType}`}
                                                        stroke={getSimulationTypeColor(
                                                            simulationType,
                                                        )}
                                                        strokeDasharray={
                                                            simulationType ===
                                                                NitrogenSimulationType.No_Rate
                                                                ? "3 3"
                                                                : "0 0"
                                                        }
                                                        strokeWidth={2}
                                                        dot={false}
                                                    />
                                                ))}
                                                <Tooltip
                                                    formatter={(
                                                        value,
                                                        name,
                                                    ) => {
                                                        return [
                                                            `${roundTo(value as number, 2)}`,
                                                            getSimulationTypeLabel(
                                                                name
                                                                    ?.toString()
                                                                    .split(
                                                                        "__",
                                                                    )[1] as NitrogenSimulationType,
                                                            ),
                                                        ];
                                                    }}
                                                    labelFormatter={(value) =>
                                                        dayjs(value).format(
                                                            "MMM D",
                                                        )
                                                    }
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </>
                            )}

                            {isConsultantOrSentinel && plan.data && (
                                <div className="flex flex-col md:flex-row md:items-center gap-2 md:justify-between">
                                    <Chip radius="sm" color="secondary">
                                        <div className="flex flex-row items-center gap-2">
                                            <div className="font-semibold">
                                                Near Term Rate (lbs):
                                            </div>
                                            <div className="flex flex-row items-end gap-2">
                                                {roundTo(
                                                    plan.data?.imagery_rate ??
                                                    0,
                                                    0,
                                                )}
                                                <span className="">
                                                    (
                                                    {roundTo(
                                                        plan.data
                                                            ?.imagery_minimum_rate ??
                                                        0,
                                                        0,
                                                    )}{" "}
                                                    -{" "}
                                                    {roundTo(
                                                        plan.data
                                                            ?.imagery_maximum_rate ??
                                                        0,
                                                        0,
                                                    )}
                                                    )
                                                </span>
                                            </div>
                                        </div>
                                    </Chip>
                                    <div className="text-xs md:text-medium text-gray-500">
                                        Click on a type to generate a
                                        prescription
                                    </div>
                                </div>
                            )}
                            {!isCorn && (
                                <div className="text-sm text-gray-500">
                                    This crop is not fully supported by the
                                    Sentinel Advisor, near term rates are still
                                    available.
                                </div>
                            )}
                            <div className=" rounded text-xs h-[500px]">
                                <TableV2
                                    data={tableData}
                                    columns={columns}
                                    sorting={sorting}
                                    setSorting={setSorting}
                                    manualSorting={false}
                                />
                            </div>
                        </>
                    )}
                    {showAdvisorSoil && (
                        <AdvisorParams
                            fieldId={fieldId}
                            onClose={() => setShowAdvisorSoil(false)}
                        />
                    )}
                </>
            )}
        </div>
    ) : (
        <div className="flex flex-col gap-2">
            <Alert
                color="warning"
                variant="flat"
                title="Advisor Not Available"
                description="Sentinel Advisor is not available for certain enrollment tiers"
                className="mb-4 max-h-48"
            />
            {isPreview && (
                <Upgrade
                    name={
                        fieldQuery.data?.service_provider?.account_manager
                            ?.full_name
                    }
                    email={
                        fieldQuery.data?.service_provider?.account_manager
                            ?.email
                    }
                />
            )}
        </div>
    );
}

export default FieldNitrogenAdvisor;
